import React, { createContext, useCallback, useContext, useMemo, useState, useLayoutEffect } from 'react';
import { axiosClient } from '../libs/axios';
import { useRestaurant } from './restaurant.context';

const initAuth = {
    isAuthentication: false,
    user: null,
    error: null,
    resetError: () => {},
    login: async ({ email, password }) => {},
    logout: async () => {},
    silentLogin: async () => {},
    forgotPassword: async () => {},
    changePassword: async () => {},
    mutateUser: () => {},
};

const Context = createContext(initAuth);

export const authenticationStatus = {
    AUTHENTICATING: 'authenticating',
    AUTHENTICATED: 'authenticated',
    FAILED: 'failed',
    FINISHED: 'finished',
};

export const useAuth = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('Hook [useAuth] must be inside [AuthProvider].');
    }

    return context;
};

export const AuthProvider = ({ children }) => {
    const { is_menu_require_auth: isRequiredAuth } = useRestaurant();
    
    const [isAuthentication, setIsAuthentication] = useState(false);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(isRequiredAuth ? authenticationStatus.AUTHENTICATING : authenticationStatus.FINISHED);

    const login = useCallback(async ({ email, password }) => {
        try {
            setIsAuthentication(true);
            setStatus(authenticationStatus.AUTHENTICATING);
            
            const { data } = await axiosClient.post(`/api/v1/menu/customer/login`, { email, password, vendor: window.vendor });

            if (error) {
                setError(null);
            }
            
            if (data.user) {
                setUser(data.user);
            }
        }
        catch (error) {
            console.dir(error);
            const { response: { data } } = error;
            setUser(null);
            setError(data.error || error.message);
        }
        finally {
            setIsAuthentication(false);
            setStatus(authenticationStatus.FINISHED);
        }
    }, [error]);

    const silentLogin = useCallback(async () => {
        try {
            setIsAuthentication(true);
            setStatus(authenticationStatus.AUTHENTICATING);
            
            const { data } = await axiosClient.get(`/api/v1/menu/customer/whoAmI`);

            if (error) {
                setError(null);
            }
            
            if (data.user) {
                setUser(data.user);
            }
        }
        catch (error) {
            console.dir(error);
            setUser(null);
            // setError(error.toString());
        }
        finally {
            setIsAuthentication(false);
            setStatus(authenticationStatus.FINISHED);
        }
    }, [error]);

    const logout = useCallback(async () => {
        try {
            setIsAuthentication(true);
            setStatus(authenticationStatus.AUTHENTICATING);
            
            const { data } = await axiosClient.get(`/api/v1/menu/customer/logout`);

            if (error) {
                setError(null);
            }
            
            if (data.success) {
                setUser(null);
            }
        }
        catch (error) {
            console.dir(error);
            const { response: { data } } = error;
            setUser(null);
            setError(data.error || error.message);
        }
        finally {
            setIsAuthentication(false);
            setStatus(authenticationStatus.FINISHED);
        }
    }, [error]);
    
    const resetError = useCallback(
        () => {
            setError(null);
        },
        [],
    );

    const forgotPassword = useCallback(async ({ email, restaurant_code }) => {
        try {
            setIsAuthentication(true);

            const { data } = await axiosClient.post(`/api/v1/menu/customer/forgot`, { email, restaurant_code });

            if (error) {
                setError(null);
            }

            // if (data.user) {
            //     setUser(data.user);
            // }
            return data;
        }
        catch (error) {
            console.dir(error);
            const { response: { data } } = error;
            setUser(null);
            setError(data.error || error.message);
        }
        finally {
            setIsAuthentication(false);
        }
    }, [error]);

    const changePassword = useCallback(async ({ password, confirm_password, restaurant_code, code }) => {
        try {
            setIsAuthentication(true);

            const { data } = await axiosClient.post(`/api/v1/menu/customer/reset`, { code, password, confirm_password, restaurant_code });

            if (error) {
                setError(null);
            }

            // if (data.user) {
            //     setUser(data.user);
            // }
            return data;
        }
        catch (error) {
            console.dir(error);
            const { response: { data } } = error;
            setUser(null);
            setError(data.error || error.message);
        }
        finally {
            setIsAuthentication(false);
        }
    }, [error]);
    
    const mutateUser = useCallback((user) => setUser(prevState => ({ ...prevState, ...user})), []);
    
    const auth = useMemo(() => ({
        ...initAuth,
        isAuthentication,
        user,
        error,
        resetError,
        login,
        logout,
        silentLogin,
        mutateUser,
        forgotPassword,
        changePassword,
        status,
    }), [user, error, isAuthentication, resetError, login, logout, silentLogin, mutateUser, forgotPassword, changePassword, status]);

    useLayoutEffect(() => {
        silentLogin();
    }, []);
    
    return (
        <Context.Provider value={auth}>
            {children}
        </Context.Provider>
    );
};

AuthProvider.displayName = 'AuthProvider';
